import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteConfig } from './index';
import BasicLayout from '@/layouts/BasicLayout';
import {
  BarChart3,
  Clapperboard,
  FileText,
  LayoutDashboard,
  MessageCircleMore,
  SettingsIcon,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { withSuspense } from './lazy';
import { DynamicRedirect } from './DynamicRedirect';
import { OperationLogSkeleton } from '@/pages/OperationLog/OperationLogSkeleton';
import { AuthorizationRecordsSkeleton } from '@/pages/AccountManagement/AuthorizationRecords/AuthorizationRecordsSkeleton';
import { AdvancedAssetGroupRecordsSkeleton } from '@/pages/AccountManagement/AdvancedAssetGroupRecords/AdvancedAssetGroupRecordsSkeleton';
import { AccountApplicationRecordsSkeleton } from '@/pages/AccountManagement/AccountApplicationRecords/AccountApplicationRecordsSkeleton';
import { AdAccountSkeleton } from '@/pages/AccountManagement/AdAccount/AdAccountSkeleton';
import { RechargeRecordsSkeleton } from '@/pages/AccountManagement/RechargeRecords/RechargeRecordsSkeleton';
import { PaymentOrdersSkeleton } from '@/pages/FinanceManagement/PaymentOrders/PaymentOrdersSkeleton';
import { AccountConsumptionSkeleton } from '@/pages/DataDashboard/AccountConsumption/AccountConsumptionSkeleton';
import { PlayletBankSkeleton } from '@/pages/Playlet/PlayletBank/PlayletBankSkeleton';
import { PlayletListSkeleton } from '@/pages/Playlet/PlayletList/PlayletListSkeleton';
import { MemberManagementSkeleton } from '@/pages/MemberPermission/MemberManagement/MemberManagementSkeleton';
import { RoleManagementSkeleton } from '@/pages/MemberPermission/RoleManagement/RoleManagementSkeleton';
import { OverviewSkeleton } from '@/pages/Overview/OverviewSkeleton';
import { AccountApplicationSkeleton } from '@/pages/AccountManagement/AccountApplication/AccountApplicationSkeleton';
import { PaymentManagementSkeleton } from '@/pages/FinanceManagement/PaymentManagement/PaymentManagementSkeleton';
import { MessageCenterSkeleton } from '@/pages/AccountCenter/MessageCenter/MessageCenterSkeleton';
import { AccountInfoSkeleton } from '@/pages/AccountCenter/AccountInfo/AccountInfoSkeleton';
import { VideoDetailsSkeleton } from '@/pages/Playlet/VideoDetails/VideoDetailsSkeleton';
import { VideoEditSkeleton } from '@/pages/Playlet/VideoDetails/VideoEditSkeleton';
import { AppealSkeleton } from '@/pages/Playlet/Appeal/AppealSkeleton';
import { UploadVideoSkeleton } from '@/pages/Playlet/UploadVideo/UploadVideoSkeleton';

// 业务页面
const Overview = lazy(() => import('@/pages/Overview'));
const AccountApplication = lazy(() => import('@/pages/AccountManagement/AccountApplication'));
const AccountApplicationRecords = lazy(
  () => import('@/pages/AccountManagement/AccountApplicationRecords')
);
const AdAccount = lazy(() => import('@/pages/AccountManagement/AdAccount'));
const RechargeRecords = lazy(() => import('@/pages/AccountManagement/RechargeRecords'));
const AuthorizationRecords = lazy(() => import('@/pages/AccountManagement/AuthorizationRecords'));
const AdvancedAssetGroupRecords = lazy(
  () => import('@/pages/AccountManagement/AdvancedAssetGroupRecords')
);
const PaymentManagement = lazy(() => import('@/pages/FinanceManagement/PaymentManagement'));
const PaymentOrders = lazy(() => import('@/pages/FinanceManagement/PaymentOrders'));
const MemberManagement = lazy(() => import('@/pages/MemberPermission/MemberManagement'));
const RoleManagement = lazy(() => import('@/pages/MemberPermission/RoleManagement'));
const AccountConsumption = lazy(() => import('@/pages/DataDashboard/AccountConsumption'));
const OperationLog = lazy(() => import('@/pages/OperationLog'));
const AuthPage = lazy(() => import('@/pages/Auth'));
const ActivatePage = lazy(() => import('@/pages/Auth/Activate'));
const MessageCenter = lazy(() => import('@/pages/AccountCenter/MessageCenter'));
const AccountInfo = lazy(() => import('@/pages/AccountCenter/AccountInfo'));
const Playlet = lazy(() => import('@/pages/Playlet/PlayletBank/index'));
const PlayletDetail = lazy(() => import('@/pages/Playlet/PlayletList'));
const PlayletVideoDetails = lazy(() => import('@/pages/Playlet/VideoDetails'));
const PlayletVideoEdit = lazy(() => import('@/pages/Playlet/VideoDetails/edit'));
const PlayletAppeal = lazy(() => import('@/pages/Playlet/Appeal'));
const PlayletUpload = lazy(() => import('@/pages/Playlet/UploadVideo'));

const NotFound = lazy(() => import('@/pages/NotFound'));

/**
 * 路由配置数组
 * 支持嵌套路由结构
 */
export const routes: RouteConfig[] = [
  {
    path: '/',
    element: <BasicLayout />,
    children: [
      {
        path: '',
        element: (
          <DynamicRedirect targetRoute={{ permCode: 'business:overview:menu', path: 'overview' }} />
        ),
        meta: {
          title: '首页',
        },
      },
      // 概览
      {
        permCode: 'business:overview:menu',
        path: 'overview',
        element: withSuspense(Overview, { fallback: <OverviewSkeleton /> }),
        meta: {
          title: '概览',
          icon: LayoutDashboard,
        },
      },
      // 开户管理
      {
        permCode: 'business:account:menu',
        path: 'account-management',
        meta: {
          title: '开户管理',
          icon: UserPlus,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/account-management/application" replace />,
          },
          {
            permCode: 'business:account_apply:sub_menu',
            path: 'application',
            element: withSuspense(AccountApplication, { fallback: <AccountApplicationSkeleton /> }),
            meta: {
              title: '开户申请',
            },
          },
          {
            permCode: 'business:account_record:sub_menu',
            path: 'application-records',
            element: withSuspense(AccountApplicationRecords, {
              fallback: <AccountApplicationRecordsSkeleton />,
            }),
            meta: {
              title: '开户申请记录',
            },
          },
          {
            permCode: 'business:ad_account:sub_menu',
            path: 'ad-account',
            element: withSuspense(AdAccount, { fallback: <AdAccountSkeleton /> }),
            meta: {
              title: '广告账户',
            },
          },
          {
            permCode: 'business:recharge:sub_menu',
            path: 'recharge-records',
            element: withSuspense(RechargeRecords, { fallback: <RechargeRecordsSkeleton /> }),
            meta: {
              title: '充值记录',
            },
          },
          {
            permCode: 'business:auth_record:sub_menu',
            path: 'authorization-records',
            element: withSuspense(AuthorizationRecords, {
              fallback: <AuthorizationRecordsSkeleton />,
            }),
            meta: {
              title: '授权记录',
            },
          },
          {
            /** 前端阶段沿用广告账户查看权限，不新增未定义的权限码 */
            permCode: 'business:ad_account:sub_menu',
            path: 'advanced-asset-group-records',
            element: withSuspense(AdvancedAssetGroupRecords, {
              fallback: <AdvancedAssetGroupRecordsSkeleton />,
            }),
            meta: {
              title: '加入高级资产组',
            },
          },
        ],
      },
      // 财务管理
      {
        permCode: 'business:finance:menu',
        path: 'finance-management',
        meta: {
          title: '财务管理',
          icon: Wallet,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/finance-management/payment-management" replace />,
          },
          {
            permCode: 'business:payment_management:sub_menu',
            path: 'payment-management',
            element: withSuspense(PaymentManagement, { fallback: <PaymentManagementSkeleton /> }),
            meta: {
              title: '支付管理',
            },
          },
          {
            permCode: 'business:payment_orders:sub_menu',
            path: 'payment-orders',
            element: withSuspense(PaymentOrders, { fallback: <PaymentOrdersSkeleton /> }),
            meta: {
              title: '支付订单',
            },
          },
        ],
      },
      // 成员&权限
      {
        permCode: 'business:member:menu',
        path: 'member-permission',
        meta: {
          title: '成员&权限',
          icon: Users,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/member-permission/member-management" replace />,
          },
          {
            permCode: 'business:member_user:sub_menu',
            path: 'member-management',
            element: withSuspense(MemberManagement, { fallback: <MemberManagementSkeleton /> }),
            meta: {
              title: '成员管理',
            },
          },
          {
            permCode: 'business:role_management:sub_menu',
            path: 'role-management',
            element: withSuspense(RoleManagement, { fallback: <RoleManagementSkeleton /> }),
            meta: {
              title: '角色管理',
            },
          },
        ],
      },
      // 数据看板
      {
        permCode: 'business:dashboard:menu',
        path: 'data-dashboard',
        meta: {
          title: '数据看板',
          icon: BarChart3,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/data-dashboard/account-consumption" replace />,
          },
          {
            permCode: 'business:account_consumption:sub_menu',
            path: 'account-consumption',
            element: withSuspense(AccountConsumption, { fallback: <AccountConsumptionSkeleton /> }),
            meta: {
              title: '账户消耗统计',
            },
          },
        ],
      },
      // 短剧
      {
        permCode: 'business:mini_series:menu',
        path: 'playlet',
        meta: {
          title: '短剧库管理',
          icon: Clapperboard,
        },
        children: [
          {
            path: '',
            element: withSuspense(Playlet, { fallback: <PlayletBankSkeleton /> }),
            meta: {
              title: '短剧库',
            },
          },
          {
            path: 'detail',
            element: withSuspense(PlayletDetail, { fallback: <PlayletListSkeleton /> }),
            meta: {
              title: '短剧列表',
            },
          },
          {
            path: 'video/:id',
            element: withSuspense(PlayletVideoDetails, { fallback: <VideoDetailsSkeleton /> }),
            meta: {
              title: '短剧详情',
            },
          },
          {
            path: 'video/:id/edit',
            element: withSuspense(PlayletVideoEdit, { fallback: <VideoEditSkeleton /> }),
            meta: {
              title: '编辑短剧',
            },
          },
          {
            path: 'appeal',
            element: withSuspense(PlayletAppeal, { fallback: <AppealSkeleton /> }),
            meta: {
              title: '提出申诉',
            },
          },
          {
            path: 'upload',
            element: withSuspense(PlayletUpload, { fallback: <UploadVideoSkeleton /> }),
            meta: {
              title: '上传短剧',
            },
          },
        ],
      },
      // 操作日志
      {
        permCode: 'business:log:menu',
        path: 'operation-log',
        element: withSuspense(OperationLog, { fallback: <OperationLogSkeleton /> }),
        meta: {
          title: '操作日志',
          icon: FileText,
        },
      },
      {
        permCode: 'business:personal_center:menu',
        path: 'account-center',
        meta: {
          title: '个人中心',
          icon: SettingsIcon,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/account-center/account-info" replace />,
          },
          {
            permCode: 'business:personal_info:menu',
            path: 'account-info',
            element: withSuspense(AccountInfo, { fallback: <AccountInfoSkeleton /> }),
            meta: {
              title: '个人信息',
            },
          },
          {
            permCode: 'business:message_center:menu',
            path: 'message-center',
            element: withSuspense(MessageCenter, { fallback: <MessageCenterSkeleton /> }),
            meta: {
              title: '消息中心',
              icon: MessageCircleMore,
            },
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: withSuspense(AuthPage),
    meta: {
      title: '登录',
      requireAuth: false,
    },
  },
  {
    path: '/activate',
    element: withSuspense(ActivatePage),
    meta: {
      title: '账号激活',
      requireAuth: false,
    },
  },
  {
    path: '/404',
    element: withSuspense(NotFound),
    meta: {
      title: '404',
      requireAuth: false,
    },
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
    meta: {
      title: '未找到',
      requireAuth: false,
    },
  },
];

/**
 * 获取用户有权限访问的路由
 * @param permCodes 用户权限码数组(userStore获取)
 * @param data 路由数据(可选，默认使用routes)
 * @param filter 额外的路由过滤函数(可选，默认返回true)
 * @returns 有权限访问的路由数组
 */
export const getHasPermissionRoutes = (
  permCodes: string[],
  data?: RouteConfig[],
  filter: (route: RouteConfig) => boolean = () => true
): RouteConfig[] => {
  if (!permCodes.length) {
    const cloneRoute = (route: RouteConfig): RouteConfig => ({
      ...route,
      meta: route.meta ? { ...route.meta } : undefined,
      children: route.children ? route.children.map(cloneRoute) : undefined,
    });

    const loginRoute = routes.find((route) => route.path === '/login');
    const activateRoute = routes.find((route) => route.path === '/activate');
    const notFoundRoute = routes.find((route) => route.path === '/404');
    const wildcardRoute = routes.find((route) => route.path === '*');

    return [
      {
        path: '/',
        element: <Navigate to="/login" replace />,
      },
      ...(loginRoute ? [cloneRoute(loginRoute)] : []),
      ...(activateRoute ? [cloneRoute(activateRoute)] : []),
      ...(notFoundRoute ? [cloneRoute(notFoundRoute)] : []),
      ...(wildcardRoute ? [cloneRoute(wildcardRoute)] : []),
    ];
  }
  const _routes: RouteConfig[] = data || routes;
  const eachArray = (routes: RouteConfig[]): RouteConfig[] => {
    return routes
      .map((route) => {
        // 创建新对象以避免修改原始路由配置
        const newRoute = { ...route };

        // 如果有 meta，也创建一个新对象，避免修改原始 meta
        if (newRoute.meta) {
          newRoute.meta = { ...newRoute.meta };
        }

        // 递归处理 children
        if (newRoute.children) {
          newRoute.children = eachArray(newRoute.children);
        }

        if (newRoute.permCode) {
          if (permCodes.includes(newRoute.permCode)) {
            return newRoute;
          }
        } else {
          // 没有权限码的路由视为非侧边栏导航跳转的路由，对于没有权限码的路由，不会在侧边栏导航中显示，但是可以通过直接访问路径来跳转
          return newRoute;
        }
      })
      .filter((r): r is RouteConfig => {
        if (!r) return false;
        return filter(r);
      });
  };
  return eachArray(_routes);
};
